// Shared full-breakdown summary card for dashboard "Financial Overview"-style rows.
// Rows: { label, value, dot?, bold? } or { divider: true }.
import React from 'react';
import { Loader2 } from 'lucide-react';
import { formatCurrency as defaultFormatCurrency } from '../../utilities/formatCurrency';

const SUMMARY_TONES = {
    green:  { card: 'bg-green-50 dark:bg-green-900/20',   icon: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-600 dark:text-green-500' },
    red:    { card: 'bg-red-50 dark:bg-red-900/20',       icon: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-500' },
    orange: { card: 'bg-orange-50 dark:bg-orange-900/20', icon: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-500' },
};

const SummaryCard = ({ title, subtitle, icon: Icon, tone, loading, data, total, totalPrefix = '', rows, footnote, formatCurrency = defaultFormatCurrency, showBar = false }) => {
    const t = SUMMARY_TONES[tone] || SUMMARY_TONES.green;

    // Bar reflects only the leading "by age" rows (those with a dot, before the first divider) —
    // Retention/Hold and other rows after the divider are excluded from the proportion.
    const barRows = [];
    if (showBar) {
        for (const row of rows) {
            if (row.divider) break;
            if (row.dot) barRows.push(row);
        }
    }
    const barTotal = barRows.reduce((sum, row) => sum + (row.value || 0), 0);

    return (
        <div className="h-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
            <div className={`flex items-center justify-between px-3 py-2 ${t.card}`}>
                <div className="flex items-center space-x-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`}>
                        <Icon className={`w-4 h-4 ${t.text}`} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{title}</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate">{subtitle}</p>
                    </div>
                </div>
                {loading ? (
                    <Loader2 className={`w-4 h-4 animate-spin ${t.text}`} />
                ) : (
                    <span className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap ml-2">
                        {totalPrefix}₹{formatCurrency(total || 0)}
                    </span>
                )}
            </div>

            <div className="px-3 py-2">
                {showBar && !loading && data && (
                    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 mb-2">
                        {barTotal > 0 ? (
                            barRows.filter((row) => row.value > 0).map((row) => (
                                <div
                                    key={row.label}
                                    className={`${row.dot} h-full`}
                                    style={{ width: `${(row.value / barTotal) * 100}%` }}
                                />
                            ))
                        ) : (
                            <div className="h-full w-full bg-gray-200 dark:bg-gray-600" />
                        )}
                    </div>
                )}
                {loading || !data ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 py-3 text-center">
                        {loading ? 'Loading…' : 'No data available'}
                    </p>
                ) : (
                    <ul className="text-xs">
                        {rows.map((row, i) => row.divider ? (
                            <li key={`d${i}`} className="my-1 border-t border-dashed border-gray-200 dark:border-gray-700" />
                        ) : (
                            <li key={row.label} className={`flex items-center justify-between ${row.sub ? 'py-0.5 pl-4 text-[11px]' : 'py-1'} ${row.highlight ? `-mx-1 px-1 rounded ${t.card}` : ''}`}>
                                <span className={`flex items-center ${row.sub ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'} ${row.bold ? 'font-semibold' : ''}`}>
                                    {row.dot && <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${row.dot}`} />}
                                    {row.label}
                                </span>
                                <span className={`tabular-nums whitespace-nowrap ml-2 ${row.bold ? 'font-bold' : 'font-medium'} text-gray-900 dark:text-white`}>
                                    ₹{formatCurrency(row.value || 0)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {footnote && !loading && data && (
                    <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">{footnote}</p>
                )}
            </div>
        </div>
    );
};

export default SummaryCard;
